/**
 * `<Select>` — короткий фиксированный выпадающий список (триггер-кнопка + белый поповер).
 *
 * Завести для коротких перечислений из коробки (статусы, режимы, язык, вид
 * отсутствия), где поиск не нужен. Сборка на radix `Select` — белый,
 * app-rendered, токенизированный поповер; в отличие от native `<select>` не
 * наследует системный `color-scheme`, поэтому всегда белый. Триггер тот же,
 * что у `<Combobox>` (`Button variant=outline size=sm`, `--radius-button`).
 * Правило «когда `<Combobox>`, когда `<Select>`» — `patterns.md
 * §Combobox / Select` (растущий список сущностей vs короткий фиксированный).
 *
 * Видимый текст (placeholder) приходит пропсом от потребителя через i18n —
 * внутри хардкода нет. RHF — через `<Controller>` (value/onChange), не register.
 */
import * as React from "react";
import { Select as SelectPrimitive } from "radix-ui";
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { formControlChromeClassName } from "@/components/ui/form-control";

export interface SelectOption {
  /** Стабильное значение опции (технический id — пользователю не показывается). */
  value: string;
  /** Видимая подпись опции. */
  label: string;
  /** Необязательный визуальный маркер перед подписью, например swatch цвета. */
  leading?: React.ReactNode;
  disabled?: boolean;
}

export interface SelectProps {
  options: SelectOption[];
  /** Выбранное значение или `null`, если ничего не выбрано. */
  value: string | null;
  onChange: (value: string) => void;
  /** Текст триггера, когда ничего не выбрано. */
  placeholder: string;
  disabled?: boolean;
  className?: string;
  /** Ширина поповера; по умолчанию подгоняется под триггер. */
  contentClassName?: string;
  "data-testid"?: string;
  id?: string;
  ref?: React.Ref<HTMLButtonElement>;
  "aria-invalid"?: React.AriaAttributes["aria-invalid"];
  "aria-describedby"?: string;
}

export function Select({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  className,
  contentClassName,
  "data-testid": testId,
  id,
  ref,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
}: SelectProps): React.ReactElement {
  const selectedOption = options.find((option) => option.value === value);
  return (
    <SelectPrimitive.Root value={value ?? undefined} onValueChange={onChange} disabled={disabled}>
      <SelectPrimitive.Trigger
        id={id}
        ref={ref}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        data-slot="select-trigger"
        data-testid={testId}
        className={cn(
          buttonVariants({ variant: "outline", size: "default" }),
          formControlChromeClassName,
          "w-full justify-between font-normal data-[placeholder]:text-muted-foreground",
          className,
        )}
      >
        <span className="flex min-w-0 items-center gap-1.5 truncate">
          <SelectPrimitive.Value placeholder={placeholder}>
            {selectedOption ? (
              <span className="flex min-w-0 items-center gap-1.5">
                {selectedOption.leading}
                <span className="truncate">{selectedOption.label}</span>
              </span>
            ) : null}
          </SelectPrimitive.Value>
        </span>
        <SelectPrimitive.Icon asChild>
          <ChevronsUpDownIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className={cn(
            "z-50 max-h-(--radix-select-content-available-height) min-w-(--radix-select-trigger-width) origin-(--radix-select-content-transform-origin) overflow-hidden rounded-[var(--radius-button)] border border-[var(--rule-line)] bg-popover p-1 text-popover-foreground shadow-md data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
            contentClassName,
          )}
        >
          <SelectPrimitive.Viewport className="p-0">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                data-testid={testId ? `${testId}-option-${option.value}` : undefined}
                className="relative flex cursor-default items-center gap-1.5 rounded-sm py-1 pr-2 pl-7 text-sm outline-hidden select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50"
              >
                <span className="absolute left-1.5 flex size-4 items-center justify-center">
                  <SelectPrimitive.ItemIndicator>
                    <CheckIcon className="size-4" aria-hidden />
                  </SelectPrimitive.ItemIndicator>
                </span>
                {option.leading}
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
